import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePursuit } from '../contexts/PursuitContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, AlertCircle, Smartphone, ShieldCheck } from 'lucide-react';
import { LATEST_POLICY_VERSION, LEGAL_PAGES_HIDDEN } from '../lib/version';
import PolicyAcceptanceModal from '../components/modals/PolicyAcceptanceModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SetupAuthenticatorPage() {
  const navigate = useNavigate();
  const { loginWithJWT } = useAuth();
  const { clearPursuitState } = usePursuit();

  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [secret, setSecret] = useState('');
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [isLoadingQr, setIsLoadingQr] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const inputRefs = useRef([]);

  const preAuthToken = sessionStorage.getItem('pre_auth_token');

  useEffect(() => {
    if (!preAuthToken) { navigate('/login'); return; }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/setup-totp`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${preAuthToken}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Setup failed');
        setQrDataUrl(data.qr_data_url);
        setSecret(data.secret);
      } catch (err) {
        setError(err.message || 'Failed to load QR code');
      } finally {
        setIsLoadingQr(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const code = digits.join('');

  const handleDigitChange = (idx, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[idx] = digit;
    setDigits(next);
    setError(null);
    if (digit && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const finishLogin = (data) => {
    sessionStorage.removeItem('pre_auth_token');
    localStorage.removeItem('auth_token');
    localStorage.setItem('auth_user', JSON.stringify(data.user));
    if (loginWithJWT) loginWithJWT(data.access_token, data.user);

    const role = data.user.role;
    const slug = data.user.organization_slug;
    let target = role === 'SUPER_ADMIN' ? '/admin/dashboard' : `/${slug}/dashboard`;

    const hasPolicy = LEGAL_PAGES_HIDDEN || (
      !!data.user.policy_accepted_at && data.user.policy_version === LATEST_POLICY_VERSION
    );
    if (!hasPolicy) {
      setPendingNavigation(target);
      setShowPolicyModal(true);
    } else {
      navigate(target);
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) { setError('Enter all 6 digits'); return; }
    setIsVerifying(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-totp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${preAuthToken}`,
        },
        credentials: 'include',
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid code');
      clearPursuitState();
      finishLogin(data);
    } catch (err) {
      setError(err.message || 'Verification failed');
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  // Auto-submit on 6 digits
  useEffect(() => {
    if (code.length === 6 && !isVerifying) handleVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-4">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-4 text-center">
            <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Smartphone className="w-7 h-7 text-teal-700" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Set up authenticator
            </CardTitle>
            <CardDescription>
              Scan this QR code with Google Authenticator, Authy, or any TOTP app.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* QR code */}
            <div className="flex justify-center">
              {isLoadingQr ? (
                <div className="w-48 h-48 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                </div>
              ) : qrDataUrl ? (
                <img src={qrDataUrl} alt="TOTP QR code" className="w-48 h-48 rounded-lg border border-slate-200" />
              ) : null}
            </div>

            {/* Manual secret */}
            {secret && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-center">
                <p className="text-xs text-slate-500 mb-1">Can't scan? Enter this key manually:</p>
                <code className="text-sm font-mono font-semibold text-slate-800 break-all">{secret}</code>
              </div>
            )}

            {/* 6-digit confirm */}
            <div>
              <p className="text-sm text-slate-600 text-center mb-3">
                Enter the 6-digit code from your app to confirm setup:
              </p>
              <form onSubmit={handleVerify}>
                <div className="flex gap-2 justify-center mb-4" onPaste={handlePaste}>
                  {digits.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={d}
                      onChange={(e) => handleDigitChange(i, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(i, e)}
                      data-testid={`totp-digit-${i}`}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-lg focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600/20 transition-colors"
                    />
                  ))}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                  disabled={isVerifying || code.length !== 6 || isLoadingQr}
                  data-testid="confirm-totp-btn"
                >
                  {isVerifying ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                  ) : (
                    <><ShieldCheck className="w-4 h-4 mr-2" /> Confirm &amp; sign in</>
                  )}
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>
      </div>

      <PolicyAcceptanceModal
        isOpen={showPolicyModal}
        onAccepted={() => {
          setShowPolicyModal(false);
          if (pendingNavigation) navigate(pendingNavigation);
        }}
      />
    </div>
  );
}
